import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Shield, Database, Zap, CheckCircle2, BarChart3, Clock, Users, FileText, Wifi, Lock, ArrowRight, Plus, Edit2, Trash2, X } from 'lucide-react'

export default function RecoveryCompliancePage() {
  const navigate = useNavigate()
  const [stats] = useState({
    dataBackups: 247,
    incidentsLogged: 12,
    policyDocuments: 34,
    complianceScore: 94,
  })

  // Recovery & Compliance Plans State
  const [plans, setPlans] = useState([
    {
      id: 1,
      name: 'Default Corporate Plan',
      companyName: 'Main Organization',
      rto: '2 hours',
      rpo: '1 hour',
      status: 'Active',
      lastUpdated: 'Mar 15, 2026',
      scope: 'Enterprise-wide',
      description: 'Standard recovery and compliance plan for all systems'
    }
  ])
  const [showPlanModal, setShowPlanModal] = useState(false)
  const [editingPlan, setEditingPlan] = useState(null)
  const [planFormData, setPlanFormData] = useState({
    companyName: '',
    name: '',
    rto: '',
    rpo: '',
    scope: 'Enterprise-wide',
    description: ''
  })

  // Plan Management Functions
  const handleCreatePlan = () => {
    setEditingPlan(null)
    setPlanFormData({
      companyName: '',
      name: '',
      rto: '',
      rpo: '',
      scope: 'Enterprise-wide',
      description: ''
    })
    setShowPlanModal(true)
  }

  const handleEditPlan = (plan) => {
    setEditingPlan(plan)
    setPlanFormData({
      companyName: plan.companyName,
      name: plan.name,
      rto: plan.rto,
      rpo: plan.rpo,
      scope: plan.scope,
      description: plan.description
    })
    setShowPlanModal(true)
  }

  const handleSavePlan = () => {
    if (!planFormData.companyName || !planFormData.name || !planFormData.rto || !planFormData.rpo) {
      alert('Please fill in all required fields')
      return
    }

    if (editingPlan) {
      // Update existing plan
      setPlans(plans.map(p => p.id === editingPlan.id ? {
        ...editingPlan,
        ...planFormData,
        lastUpdated: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
      } : p))
    } else {
      // Create new plan
      setPlans([...plans, {
        id: Date.now(),
        ...planFormData,
        status: 'Active',
        lastUpdated: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
      }])
    }
    setShowPlanModal(false)
  }

  const handleDeletePlan = (id) => {
    if (confirm('Are you sure you want to delete this plan?')) {
      setPlans(plans.filter(p => p.id !== id))
    }
  }

  const features = [
    {
      title: '🛡️ Data Loss Prevention (DLP)',
      description: 'Monitor, detect, and prevent unauthorized data transfers and sensitive information leaks',
      icon: Shield,
      color: 'from-red-600 to-red-500',
      stats: 'Real-time Monitoring',
      path: '/dlp'
    },
    {
      title: '💾 Data Backup & Recovery',
      description: 'Automated daily backups with point-in-time recovery and disaster recovery capabilities',
      icon: Database,
      color: 'from-blue-600 to-blue-500',
      stats: '247 Backups',
      path: '/backups'
    },
    {
      title: '📋 Data Retention Policies',
      description: 'Define and enforce data retention schedules with automatic deletion and archival',
      icon: FileText,
      color: 'from-cyan-600 to-cyan-500',
      stats: '34 Policies',
      path: '/retention'
    },
    {
      title: '🚨 Incident Response Plan',
      description: 'Document, manage, and respond to security incidents with automated workflows',
      icon: AlertTriangle,
      color: 'from-orange-600 to-orange-500',
      stats: '12 Logged Incidents',
      path: '/incidents'
    },
    {
      title: '🏢 Business Continuity Plan',
      description: 'Create and maintain business continuity strategies and disaster recovery procedures',
      icon: Zap,
      color: 'from-indigo-600 to-indigo-500',
      stats: 'BCP Active',
      path: '/continuity'
    },
    {
      title: '📊 Compliance Dashboard',
      description: 'Track compliance status for GDPR, HIPAA, SOC2, and industry regulations',
      icon: BarChart3,
      color: 'from-teal-600 to-teal-500',
      stats: '94% Compliant',
      path: '/compliance'
    },
    {
      title: '🔐 Access Control & Encryption',
      description: 'Manage data encryption, access logs, and multi-factor authentication',
      icon: Lock,
      color: 'from-purple-600 to-purple-500',
      stats: 'All Data Encrypted',
      path: '/encryption'
    },
    {
      title: '📈 Audit & Logging',
      description: 'Comprehensive audit trails for all system activities and data access events',
      icon: CheckCircle2,
      color: 'from-amber-600 to-amber-500',
      stats: '1.2M Events/Day',
      path: '/audit-logs'
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-red-600 via-orange-600 to-red-500 p-8 text-white shadow-2xl border border-red-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-orange-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <AlertTriangle className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Recovery & Compliance Plan</h1>
                </div>
                <p className="text-red-100 text-lg font-semibold">Enterprise Data Protection, Compliance & Business Continuity</p>
                <p className="text-red-200 text-sm mt-1">DLP, Data Retention, Incident Response, GDPR/HIPAA/SOC2 Compliance</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.complianceScore}%</div>
                <div className="text-sm text-red-100">Compliance Score</div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Data Backups', value: stats.dataBackups, icon: Database, color: 'from-blue-600 to-blue-500', textColor: 'text-blue-400' },
            { label: 'Incidents Logged', value: stats.incidentsLogged, icon: AlertTriangle, color: 'from-orange-600 to-orange-500', textColor: 'text-orange-400' },
            { label: 'Policy Documents', value: stats.policyDocuments, icon: FileText, color: 'from-indigo-600 to-indigo-500', textColor: 'text-indigo-400' },
            { label: 'Compliance Status', value: `${stats.complianceScore}%`, icon: CheckCircle2, color: 'from-green-600 to-green-500', textColor: 'text-green-400' },
          ].map((stat, idx) => {
            const Icon = stat.icon
            return (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
                    <p className={`${stat.textColor} text-4xl font-black mt-3`}>{stat.value}</p>
                  </div>
                  <div className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-lg shadow-blue-500/20`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Recovery & Compliance Features */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center space-x-3">
            <Shield className="w-7 h-7 text-red-400" />
            <span>Recovery & Compliance Tools</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, idx) => {
              const Icon = feature.icon
              return (
                <div
                  key={idx}
                  onClick={() => navigate(feature.path)}
                  className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all cursor-pointer shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1"
                >
                  <div className={`h-1 w-full bg-gradient-to-r ${feature.color} rounded-full mb-4 shadow-lg`}></div>

                  <div className={`bg-gradient-to-br ${feature.color} p-3 rounded-lg w-fit mb-4 shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  <h3 className="font-bold text-white mb-2 text-lg">{feature.title}</h3>
                  <p className="text-blue-300 text-sm mb-4">{feature.description}</p>

                  <div className="flex items-center justify-between pt-4 border-t border-blue-500/20">
                    <span className="text-cyan-400 text-sm font-semibold">{feature.stats}</span>
                    <button className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRight className="w-4 h-4 text-blue-400" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Compliance & Recovery Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Compliance Status */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span>Compliance Framework Status</span>
            </h3>
            <div className="space-y-3">
              {[
                { framework: 'GDPR Compliance', status: 'Fully Compliant', score: 98 },
                { framework: 'HIPAA Compliance', status: 'Compliant', score: 94 },
                { framework: 'SOC 2 Type II', status: 'Certified', score: 97 },
                { framework: 'ISO 27001', status: 'Compliant', score: 92 },
                { framework: 'Data Privacy', status: 'Compliant', score: 95 },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-700/50 rounded-lg p-4 border border-green-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white font-semibold text-sm">{item.framework}</p>
                    <span className="bg-green-500/20 text-green-300 text-xs font-bold px-2 py-0.5 rounded">
                      {item.status}
                    </span>
                  </div>
                  <div className="w-full bg-slate-600 rounded-full h-2">
                    <div className="h-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full" style={{width: `${item.score}%`}}></div>
                  </div>
                  <p className="text-green-400 text-xs mt-1 font-semibold">{item.score}% Compliant</p>
                </div>
              ))}
            </div>
          </div>

          {/* Critical Alerts & Actions */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              <span>Security Alerts & Actions</span>
            </h3>
            <div className="space-y-3">
              {[
                { alert: 'Backup Verification', action: 'Scheduled for today', priority: 'HIGH', dueDate: '10:00 AM' },
                { alert: 'Data Retention Audit', action: 'Review retention policies', priority: 'MEDIUM', dueDate: '2 days' },
                { alert: 'Incident Log Review', action: '12 incidents require analysis', priority: 'MEDIUM', dueDate: '3 days' },
                { alert: 'BCP Testing', action: 'Quarterly DR test scheduled', priority: 'HIGH', dueDate: '1 week' },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-700/50 rounded-lg p-3 border border-orange-500/20">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white font-semibold text-sm">{item.alert}</p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                      item.priority === 'HIGH' ? 'bg-red-500/20 text-red-300' : 'bg-yellow-500/20 text-yellow-300'
                    }`}>
                      {item.priority}
                    </span>
                  </div>
                  <p className="text-blue-300 text-xs mb-1">{item.action}</p>
                  <p className="text-cyan-400 text-xs font-semibold">Due: {item.dueDate}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Custom Recovery & Compliance Plans */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
              <FileText className="w-6 h-6 text-cyan-400" />
              <span>Custom Recovery & Compliance Plans</span>
            </h2>
            <button
              onClick={handleCreatePlan}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold py-2 px-4 rounded-lg flex items-center space-x-2 transition-all shadow-lg hover:shadow-cyan-500/50"
            >
              <Plus className="w-5 h-5" />
              <span>Create Plan</span>
            </button>
          </div>

          {plans.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4 opacity-50" />
              <p className="text-slate-400 text-lg">No recovery & compliance plans yet</p>
              <p className="text-slate-500 text-sm mt-2">Create your first custom plan to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {plans.map((plan) => (
                <div key={plan.id} className="bg-slate-700/50 rounded-lg p-5 border border-cyan-500/20 hover:border-cyan-400/40 transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-white font-bold text-lg mb-1">{plan.name}</h3>
                      <p className="text-blue-300 text-sm mb-2">Company: <span className="text-cyan-400 font-semibold">{plan.companyName}</span></p>
                      <p className="text-slate-400 text-sm">{plan.description}</p>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleEditPlan(plan)}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white p-2 rounded-lg transition-all"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeletePlan(plan.id)}
                        className="bg-red-600 hover:bg-red-500 text-white p-2 rounded-lg transition-all"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-3 border-t border-slate-600">
                    <div>
                      <span className="text-slate-400 text-xs block">Scope</span>
                      <p className="text-cyan-400 font-semibold text-sm mt-1">{plan.scope}</p>
                    </div>
                    <div>
                      <span className="text-slate-400 text-xs block">RTO</span>
                      <p className="text-amber-400 font-semibold text-sm mt-1">{plan.rto}</p>
                    </div>
                    <div>
                      <span className="text-slate-400 text-xs block">RPO</span>
                      <p className="text-amber-400 font-semibold text-sm mt-1">{plan.rpo}</p>
                    </div>
                    <div>
                      <span className="text-slate-400 text-xs block">Status</span>
                      <p className="text-green-400 font-semibold text-sm mt-1">{plan.status}</p>
                    </div>
                    <div>
                      <span className="text-slate-400 text-xs block">Last Updated</span>
                      <p className="text-slate-300 font-semibold text-sm mt-1">{plan.lastUpdated}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recovery & Continuity Plans */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
            <Zap className="w-6 h-6 text-blue-400" />
            <span>Business Continuity & Disaster Recovery</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: '📋 BCP Documentation', status: 'Current', updated: 'Mar 15, 2026', rto: '4 hours' },
              { title: '🔄 Disaster Recovery Plan', status: 'Active', updated: 'Mar 10, 2026', rto: '2 hours' },
              { title: '💾 Data Backup Schedule', status: 'Automated', updated: 'Daily', rto: '1 hour' },
              { title: '🏢 Failover Procedures', status: 'Tested', updated: 'Feb 28, 2026', rto: '15 min' },
              { title: '👥 Team Recovery Contacts', status: 'Updated', updated: 'Mar 1, 2026', rto: 'N/A' },
              { title: '🎯 Recovery Objectives', status: 'Defined', updated: 'Jan 15, 2026', rto: 'Yes' },
            ].map((item, idx) => (
              <div key={idx} className="bg-slate-700/50 rounded-lg p-4 border border-blue-500/20">
                <p className="text-white font-bold mb-2">{item.title}</p>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-300">Status:</span>
                    <span className="text-green-400 font-semibold">{item.status}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-300">Updated:</span>
                    <span className="text-cyan-400">{item.updated}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-300">RTO:</span>
                    <span className="text-amber-400 font-semibold">{item.rto}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Plan Modal */}
        {showPlanModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-cyan-500/20 max-w-2xl w-full p-8 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">
                  {editingPlan ? 'Edit Recovery & Compliance Plan' : 'Create New Recovery & Compliance Plan'}
                </h3>
                <button
                  onClick={() => setShowPlanModal(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Company Name */}
                <div>
                  <label className="block text-white font-semibold mb-2">Company Name *</label>
                  <input
                    type="text"
                    value={planFormData.companyName}
                    onChange={(e) => setPlanFormData({...planFormData, companyName: e.target.value})}
                    placeholder="Enter company name"
                    className="w-full bg-slate-700/50 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                  />
                </div>

                {/* Plan Name */}
                <div>
                  <label className="block text-white font-semibold mb-2">Plan Name *</label>
                  <input
                    type="text"
                    value={planFormData.name}
                    onChange={(e) => setPlanFormData({...planFormData, name: e.target.value})}
                    placeholder="e.g., Primary Production Recovery Plan"
                    className="w-full bg-slate-700/50 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-white font-semibold mb-2">Description</label>
                  <textarea
                    value={planFormData.description}
                    onChange={(e) => setPlanFormData({...planFormData, description: e.target.value})}
                    placeholder="Describe your recovery and compliance plan"
                    rows="3"
                    className="w-full bg-slate-700/50 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 resize-none"
                  />
                </div>

                {/* RTO & RPO */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white font-semibold mb-2">Recovery Time Objective (RTO) *</label>
                    <input
                      type="text"
                      value={planFormData.rto}
                      onChange={(e) => setPlanFormData({...planFormData, rto: e.target.value})}
                      placeholder="e.g., 2 hours, 4 hours"
                      className="w-full bg-slate-700/50 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                    />
                  </div>
                  <div>
                    <label className="block text-white font-semibold mb-2">Recovery Point Objective (RPO) *</label>
                    <input
                      type="text"
                      value={planFormData.rpo}
                      onChange={(e) => setPlanFormData({...planFormData, rpo: e.target.value})}
                      placeholder="e.g., 1 hour, 30 minutes"
                      className="w-full bg-slate-700/50 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                    />
                  </div>
                </div>

                {/* Scope */}
                <div>
                  <label className="block text-white font-semibold mb-2">Plan Scope</label>
                  <select
                    value={planFormData.scope}
                    onChange={(e) => setPlanFormData({...planFormData, scope: e.target.value})}
                    className="w-full bg-slate-700/50 border border-blue-500/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500/50"
                  >
                    <option>Enterprise-wide</option>
                    <option>Department-specific</option>
                    <option>Critical Systems Only</option>
                    <option>Custom Scope</option>
                  </select>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowPlanModal(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-4 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePlan}
                  className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold py-2 px-4 rounded-lg transition-all shadow-lg hover:shadow-cyan-500/50"
                >
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
